import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Aviso,
  Botao,
  Cartao,
  CartaoIndicador,
  EstadoVazio,
  Modal,
  Selo,
  Stepper,
  Tabela,
  ValorFiscal,
  ValorPercentual,
  formatarMoeda,
  useDialogo,
  type ColunaTabela,
} from '../../componentes/ui';
import { BotaoExtrairXlsx } from '../../componentes/BotaoExtrairXlsx';
import { MeusPedidos, invalidarPedidos } from '../../componentes/PedidosDeFechamento';
import { useParticipacao } from '../../hooks/useParticipacao';
import { useEmpresaCorrente } from '../../hooks/useSessao';
import { baixarArquivo, requisicaoAutenticada } from '../../servicos/http';
import { assessoraEmAlgum, pedirFechamento } from '../../servicos/participacao';
import { diaLocal, type ColunaDaPlanilha } from '../../servicos/planilha';
import { ErroDaApi } from '../ErroDaApi';
import { Pagina } from '../Carteira';
import {
  Item,
  ROTULO_DO_DOCUMENTO,
  ROTULO_DO_STATUS,
  ROTULO_DO_TIPO,
  TOM_DA_ACAO,
  TOM_DO_STATUS,
  emBr,
  passosDa,
} from './comum';
import { SimulacaoParaOCliente } from './SimulacaoParaOCliente';
import type { NegociacaoDaParte, PapelNaNegociacao, ParcelaPontual } from './tipos';

/**
 * ICMS Pontual de quem não é admin: as negociações em que a empresa escolhida
 * é parte, cada uma na visão do papel dela.
 *
 * ## O que cada papel vê
 *
 * O servidor decide (`GET /pontual/minhas/negociacoes`): o **comprador** vê o
 * que paga e as parcelas dele; o **fornecedor**, também o que recebe, o spread
 * e as parcelas dos honorários, separadas das do comprador; o **assessor**, a
 * conta inteira. O que não vem chega como `null`, e a tela mostra travessão —
 * nunca um zero que pareça valor.
 *
 * ## O que cada papel faz
 *
 * Acompanhar o andamento, baixar os documentos da negociação (contrato,
 * requerimento, certificado) e extrair a lista. O assessor também simula para
 * os clientes da carteira e pede o fechamento — da simulação livre ou da que
 * a operação já gravou. Fechar, protocolar e dar baixa continuam sendo atos da
 * operação, e a API recusa o resto.
 */

const CHAVE = ['pontual-da-parte'] as const;

const ROTULO_DO_PAPEL: Readonly<Record<PapelNaNegociacao, string>> = {
  COMPRADOR: 'comprador',
  FORNECEDOR: 'fornecedor',
  ASSESSOR: 'assessor',
};

const papeisPorExtenso = (papeis: readonly PapelNaNegociacao[]): string =>
  papeis.map((p) => ROTULO_DO_PAPEL[p]).join(' e ');

const emAberto = (p: ParcelaPontual): boolean => p.status !== 'PAGA' && p.status !== 'CANCELADA';

const COLUNAS_DA_PLANILHA: readonly ColunaDaPlanilha<NegociacaoDaParte>[] = [
  { titulo: 'Fornecedor', largura: 36, valor: (n) => n.fornecedorNome },
  { titulo: 'Comprador', largura: 36, valor: (n) => n.compradorNome },
  { titulo: 'Assessor', largura: 30, valor: (n) => n.parceiroNome },
  { titulo: 'Papel da empresa', largura: 18, valor: (n) => papeisPorExtenso(n.papeis) },
  {
    titulo: 'Tipo de crédito',
    largura: 14,
    valor: (n) => ROTULO_DO_TIPO[n.tipoCredito] ?? n.tipoCredito,
  },
  { titulo: 'Situação', largura: 16, valor: (n) => ROTULO_DO_STATUS[n.status] ?? n.status },
  { titulo: 'Próximo passo', largura: 30, valor: (n) => n.proximaAcao.resumo },
  { titulo: 'Protocolo', largura: 18, valor: (n) => n.protocoloTransferencia },
  // Cada deságio pelo lado de quem o dá, como na tabela do Pontual (14/10/2026).
  { titulo: '$ Negociação', formato: 'moeda', valor: (n) => n.valorFace },
  { titulo: 'Comprador paga', formato: 'moeda', valor: (n) => n.valorVenda },
  { titulo: '% Deságio compra', formato: 'percentual', valor: (n) => n.desagioVendaPercentual },
  { titulo: 'Fornecedor recebe', formato: 'moeda', valor: (n) => n.valorCompra },
  { titulo: '% Deságio venda', formato: 'percentual', valor: (n) => n.desagioCompraPercentual },
  { titulo: 'Spread (honorários)', formato: 'moeda', valor: (n) => n.spread },
  {
    titulo: 'Pagamento',
    largura: 24,
    valor: (n) =>
      [n.parcelas === 1 ? 'À vista' : `${n.parcelas}x`, n.regraPagamentoDescricao]
        .filter(Boolean)
        .join(' — '),
  },
  { titulo: 'Transferência', formato: 'data', valor: (n) => diaLocal(n.dataTransferencia) },
  { titulo: 'Criada em', formato: 'data', valor: (n) => diaLocal(n.criadaEm) },
];

export function PontualDaParte(): JSX.Element {
  const empresa = useEmpresaCorrente();
  const participacao = useParticipacao();
  const queryClient = useQueryClient();
  const { pedirTexto } = useDialogo();
  const [abertaId, setAbertaId] = useState<string | null>(null);
  const [simulando, setSimulando] = useState(false);
  // Quem assessora em qualquer serviço simula para os clientes aqui também.
  const assessor = assessoraEmAlgum(participacao.data);

  const negociacoes = useQuery({
    queryKey: [...CHAVE, empresa],
    queryFn: () =>
      requisicaoAutenticada<NegociacaoDaParte[]>(
        `/pontual/minhas/negociacoes?empresa=${encodeURIComponent(empresa ?? '')}`,
      ),
    enabled: empresa !== null,
  });

  /** O pedido sobre uma simulação que a operação já gravou. */
  const pedir = useMutation({
    mutationFn: (entrada: { readonly n: NegociacaoDaParte; readonly observacao: string }) =>
      pedirFechamento({
        empresa: empresa ?? '',
        servico: 'PONTUAL',
        clienteId: entrada.n.compradorId,
        negociacaoPontualId: entrada.n.id,
        ...(entrada.observacao.trim() === '' ? {} : { observacao: entrada.observacao.trim() }),
      }),
    onSuccess: () => invalidarPedidos(queryClient),
  });

  const pedirParaA = (n: NegociacaoDaParte): void => {
    void (async () => {
      const observacao = await pedirTexto({
        titulo: 'Pedir o fechamento desta negociação?',
        descricao:
          'A operação é avisada e segue com o contrato: combina o pagamento, gera e envia para assinatura.',
        rotulo: 'Observação para a operação (opcional)',
        multilinha: true,
        rotuloConfirmar: 'Pedir fechamento',
      });
      if (observacao !== null) pedir.mutate({ n, observacao });
    })();
  };

  const lista = negociacoes.data ?? [];
  const andamento = lista.filter(
    (n) => n.status === 'FECHADA' || n.status === 'PROTOCOLADA' || n.status === 'TRANSFERIDA',
  );
  const parcelasEmAberto = lista.flatMap((n) => n.parcelasPagamento).filter(emAberto);
  const doComprador = parcelasEmAberto.filter((p) => p.natureza === 'PAGAMENTO_COMPRADOR');
  const deHonorarios = parcelasEmAberto.filter((p) => p.natureza === 'HONORARIOS_ASSESSOR');
  const soma = (ps: readonly ParcelaPontual[]): number => ps.reduce((t, p) => t + p.valor, 0);
  // Coluna só aparece se alguma linha tiver o valor: para quem só compra, as
  // colunas do lado do fornecedor seriam um travessão atrás do outro.
  const veFornecedor = lista.some((n) => n.valorCompra !== null);

  const colunas: readonly ColunaTabela<NegociacaoDaParte>[] = [
    {
      chave: 'partes',
      titulo: 'Negociação',
      larguraMinima: '16rem',
      celula: (n) => (
        <span className="flex flex-col">
          <button
            type="button"
            onClick={() => setAbertaId(n.id)}
            className="text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
          >
            {n.fornecedorNome ?? 'Fornecedor'} → {n.compradorNome ?? 'Comprador'}
          </button>
          <span className="text-xs text-conteudo-sutil">
            {ROTULO_DO_TIPO[n.tipoCredito] ?? n.tipoCredito}
            {n.parceiroNome !== null && ` · assessor ${n.parceiroNome}`}
          </span>
          <span className="text-xs text-conteudo-secundario">
            Sua empresa: {papeisPorExtenso(n.papeis)}
          </span>
        </span>
      ),
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '12rem',
      // O pedido de fechamento mora aqui, embaixo do próximo passo: é o passo
      // do assessor, e uma coluna só para ele empurrava a tabela para fora da tela.
      celula: (n) => (
        <span className="flex flex-col items-start gap-1">
          <Selo tom={TOM_DO_STATUS[n.status] ?? 'neutro'}>
            {ROTULO_DO_STATUS[n.status] ?? n.status}
          </Selo>
          <span
            className={
              n.proximaAcao.tom === 'atencao'
                ? 'text-xs text-atencao'
                : 'text-xs text-conteudo-secundario'
            }
          >
            {n.proximaAcao.resumo}
          </span>
          {n.papeis.includes('ASSESSOR') &&
            n.status === 'SIMULACAO' &&
            (n.fechamentoSolicitado ? (
              <Selo tom="atencao" tamanho="pequeno">
                Fechamento pedido
              </Selo>
            ) : (
              <Botao
                variante="secundario"
                tamanho="pequeno"
                carregando={pedir.isPending && pedir.variables?.n.id === n.id}
                onClick={() => pedirParaA(n)}
              >
                Pedir fechamento
              </Botao>
            ))}
        </span>
      ),
    },
    {
      chave: 'valorFace',
      titulo: 'Face',
      numerica: true,
      larguraMinima: '8rem',
      celula: (n) => <ValorFiscal valor={n.valorFace} tamanho="pequeno" />,
    },
    {
      chave: 'valorVenda',
      titulo: 'Comprador paga',
      numerica: true,
      larguraMinima: '9rem',
      celula: (n) => <ValorFiscal valor={n.valorVenda} tamanho="pequeno" />,
    },
    ...(veFornecedor
      ? ([
          {
            chave: 'valorCompra',
            titulo: 'Fornecedor recebe',
            numerica: true,
            larguraMinima: '9rem',
            celula: (n) => <ValorFiscal valor={n.valorCompra} tamanho="pequeno" />,
          },
          {
            chave: 'spread',
            titulo: 'Spread',
            numerica: true,
            larguraMinima: '8rem',
            celula: (n) =>
              n.spread === null ? (
                <span className="text-conteudo-sutil">—</span>
              ) : (
                <ValorFiscal valor={n.spread} variante="economia" tamanho="pequeno" />
              ),
          },
        ] satisfies ColunaTabela<NegociacaoDaParte>[])
      : []),
    {
      chave: 'pagamento',
      titulo: 'Pagamento',
      larguraMinima: '9rem',
      celula: (n) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-secundario">
            {n.parcelas === 1 ? 'À vista' : `${n.parcelas}×`}
          </span>
          {n.regraPagamentoDescricao !== null && (
            <span className="text-xs text-conteudo-sutil">{n.regraPagamentoDescricao}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <Pagina
      titulo="Créditos ICMS Pontual"
      descricao="As negociações de crédito em que esta empresa é parte — como comprador, fornecedor ou assessor. Cada uma mostra o que cabe ao papel da empresa nela."
      contagem={lista.length}
      acao={
        <div className="flex flex-wrap items-center gap-3">
          <BotaoExtrairXlsx
            titulo="Negociações - ICMS Pontual"
            disabled={negociacoes.data === undefined}
            colunas={COLUNAS_DA_PLANILHA}
            linhas={lista}
          />
          {assessor && <Botao onClick={() => setSimulando(true)}>Simular para um cliente</Botao>}
        </div>
      }
    >
      {negociacoes.isError && (
        <ErroDaApi titulo="Não foi possível carregar" erro={negociacoes.error} />
      )}
      {pedir.isError && <ErroDaApi titulo="Não foi possível pedir" erro={pedir.error} />}

      <div className="grid gap-4 sm:grid-cols-3">
        <CartaoIndicador
          rotulo="Em andamento"
          valor={<ValorFiscal valor={andamento.length} moeda={false} casas={0} tamanho="grande" />}
          detalhe="Fechadas, protocoladas ou transferidas, ainda sem pagamento completo"
        />
        <CartaoIndicador
          rotulo="Crédito negociado"
          valor={
            <ValorFiscal
              valor={lista
                .filter((n) => n.status !== 'CANCELADA')
                .reduce((total, n) => total + n.valorFace, 0)}
              tamanho="grande"
            />
          }
          detalhe="Valor de face das negociações vivas"
        />
        <CartaoIndicador
          rotulo="Parcelas em aberto"
          valor={
            <ValorFiscal valor={parcelasEmAberto.length} moeda={false} casas={0} tamanho="grande" />
          }
          detalhe={
            parcelasEmAberto.length === 0
              ? 'Nada a pagar nem a receber agora'
              : [
                  doComprador.length > 0 ? `Comprador ${formatarMoeda(soma(doComprador))}` : null,
                  deHonorarios.length > 0
                    ? `honorários ${formatarMoeda(soma(deHonorarios))}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
        />
      </div>

      {!negociacoes.isPending && lista.length === 0 ? (
        <EstadoVazio
          titulo="Nenhuma negociação de Pontual"
          descricao={
            assessor
              ? 'Ainda não há negociação com a sua assessoria. Simule para um cliente da carteira e peça o fechamento: a operação monta a negociação com os seus números.'
              : 'Esta empresa ainda não é parte de nenhuma negociação de crédito.'
          }
          {...(assessor
            ? { acao: <Botao onClick={() => setSimulando(true)}>Simular para um cliente</Botao> }
            : {})}
        />
      ) : (
        <Cartao como="section" corpoSemPreenchimento>
          <Tabela
            legenda="Negociações de ICMS Pontual da empresa"
            densidade="compacta"
            colunas={colunas}
            dados={lista}
            chaveDaLinha={(n) => n.id}
            chaveDeCabecalhoDaLinha="partes"
            carregando={negociacoes.isPending}
          />
        </Cartao>
      )}

      {assessor && <MeusPedidos servico="PONTUAL" />}

      <DetalheDaParte
        negociacaoId={abertaId}
        aoFechar={() => setAbertaId(null)}
        aoPedir={pedirParaA}
        pedindo={pedir.isPending}
      />
      <SimulacaoParaOCliente aberto={simulando} aoFechar={() => setSimulando(false)} />
    </Pagina>
  );
}

/* ─── Detalhe ─────────────────────────────────────────────────────────────── */

/**
 * Uma negociação, só leitura: os números do papel, a trilha dos quatro passos,
 * os documentos para baixar e as parcelas — do comprador e, para quem vê o lado
 * do fornecedor, as dos honorários, separadas.
 */
function DetalheDaParte(props: {
  readonly negociacaoId: string | null;
  readonly aoFechar: () => void;
  readonly aoPedir: (n: NegociacaoDaParte) => void;
  readonly pedindo: boolean;
}): JSX.Element {
  const empresa = useEmpresaCorrente();
  const id = props.negociacaoId;

  const negociacao = useQuery({
    queryKey: [...CHAVE, empresa, id],
    queryFn: () =>
      requisicaoAutenticada<NegociacaoDaParte>(
        `/pontual/minhas/negociacoes/${id ?? ''}?empresa=${encodeURIComponent(empresa ?? '')}`,
      ),
    enabled: id !== null && empresa !== null,
  });
  const dados = negociacao.data;

  const colunas: readonly ColunaTabela<ParcelaPontual>[] = [
    {
      chave: 'natureza',
      titulo: 'Quem paga',
      larguraMinima: '13rem',
      celula: (p) => (
        <span className="flex flex-col">
          <span className="text-sm text-conteudo-primario">
            {p.natureza === 'HONORARIOS_ASSESSOR' ? 'Honorários do assessor' : 'Comprador'}
          </span>
          <span className="text-xs text-conteudo-sutil">parcela {p.numeroParcela}</span>
        </span>
      ),
    },
    {
      chave: 'dataVencimento',
      titulo: 'Vencimento',
      larguraMinima: '8rem',
      celula: (p) => <span className="valor-fiscal text-sm">{emBr(p.dataVencimento)}</span>,
    },
    {
      chave: 'valor',
      titulo: 'Valor',
      numerica: true,
      larguraMinima: '9rem',
      celula: (p) => <ValorFiscal valor={p.valor} tamanho="pequeno" />,
    },
    {
      chave: 'status',
      titulo: 'Situação',
      larguraMinima: '9rem',
      celula: (p) => (
        <span className="flex flex-col gap-0.5">
          <Selo
            tom={
              p.status === 'PAGA'
                ? 'positivo'
                : p.status === 'CANCELADA'
                  ? 'neutro'
                  : p.status === 'ATRASADA'
                    ? 'critico'
                    : 'progresso'
            }
          >
            {p.status === 'PAGA'
              ? 'Paga'
              : p.status === 'CANCELADA'
                ? 'Cancelada'
                : p.status === 'ATRASADA'
                  ? 'Atrasada'
                  : 'Pendente'}
          </Selo>
          {p.dataPagamento !== null && (
            <span className="text-xs text-conteudo-sutil">
              em {emBr(diaLocal(p.dataPagamento) ?? '')}
            </span>
          )}
        </span>
      ),
    },
  ];

  return (
    <Modal
      aberto={id !== null}
      aoFechar={props.aoFechar}
      titulo="Negociação de crédito"
      descricao={
        dados === undefined
          ? undefined
          : `${dados.fornecedorNome ?? 'Fornecedor'} → ${dados.compradorNome ?? 'Comprador'} · sua empresa: ${papeisPorExtenso(dados.papeis)}`
      }
      largura="larga"
      rodape={<Botao onClick={props.aoFechar}>Fechar</Botao>}
    >
      {negociacao.isError && (
        <ErroDaApi titulo="Não foi possível carregar" erro={negociacao.error} />
      )}
      {dados === undefined ? (
        !negociacao.isError && (
          <p className="py-8 text-center text-sm text-conteudo-sutil">Carregando…</p>
        )
      ) : (
        <div className="flex flex-col gap-5">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
            <Item rotulo="Face">
              <ValorFiscal valor={dados.valorFace} tamanho="pequeno" />
            </Item>
            <Item rotulo="Comprador paga">
              <span className="flex flex-col">
                <ValorFiscal valor={dados.valorVenda} tamanho="pequeno" />
                <span className="text-xs text-conteudo-sutil">
                  deságio{' '}
                  <ValorPercentual percentual={dados.desagioVendaPercentual} tamanho="pequeno" />
                </span>
              </span>
            </Item>
            {dados.valorCompra !== null && (
              <Item rotulo="Fornecedor recebe">
                <span className="flex flex-col">
                  <ValorFiscal valor={dados.valorCompra} tamanho="pequeno" />
                  {dados.desagioCompraPercentual !== null && (
                    <span className="text-xs text-conteudo-sutil">
                      deságio{' '}
                      <ValorPercentual
                        percentual={dados.desagioCompraPercentual}
                        tamanho="pequeno"
                      />
                    </span>
                  )}
                </span>
              </Item>
            )}
            {dados.spread !== null && (
              <Item rotulo="Spread (honorários)">
                <span className="flex flex-col">
                  <ValorFiscal valor={dados.spread} variante="economia" tamanho="pequeno" />
                  <span className="text-xs text-conteudo-sutil">
                    pagos pelo{' '}
                    {dados.honorariosPagador === 'COMPRADOR' ? 'comprador' : 'fornecedor'}
                  </span>
                </span>
              </Item>
            )}
            <Item rotulo="Pagamento">
              <span className="flex flex-col text-sm text-conteudo-secundario">
                {dados.parcelas === 1 ? 'À vista' : `${dados.parcelas} parcelas`}
                {dados.regraPagamentoDescricao !== null && (
                  <span className="text-xs text-conteudo-sutil">
                    {dados.regraPagamentoDescricao}
                  </span>
                )}
              </span>
            </Item>
            {dados.protocoloTransferencia !== null && (
              <Item rotulo="Protocolo">
                <span className="valor-fiscal text-sm">{dados.protocoloTransferencia}</span>
              </Item>
            )}
          </dl>

          <Stepper rotulo="Passos da negociação" passos={passosDa(dados)} />

          <Aviso
            tom={TOM_DA_ACAO[dados.proximaAcao.tom] ?? 'informacao'}
            titulo={dados.proximaAcao.resumo}
          >
            {dados.proximaAcao.detalhe}
          </Aviso>

          {dados.papeis.includes('ASSESSOR') && dados.status === 'SIMULACAO' && (
            <section className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-borda p-4">
              <p className="text-sm text-conteudo-secundario">
                {dados.fechamentoSolicitado
                  ? 'Fechamento pedido — a operação já foi avisada.'
                  : 'Com os valores aprovados pelo cliente, peça o fechamento à operação.'}
              </p>
              {!dados.fechamentoSolicitado && (
                <Botao carregando={props.pedindo} onClick={() => props.aoPedir(dados)}>
                  Pedir fechamento
                </Botao>
              )}
            </section>
          )}

          {dados.documentos.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Documentos ({dados.documentos.length})
              </h3>
              <ul className="flex flex-col divide-y divide-borda rounded-card border border-borda">
                {dados.documentos.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                    <span className="flex min-w-0 flex-col">
                      <button
                        type="button"
                        onClick={() => void baixarArquivo(`/pontual/documentos/${d.id}`, d.nome)}
                        className="truncate text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
                      >
                        {d.nome}
                      </button>
                      <span className="text-xs text-conteudo-sutil">
                        {ROTULO_DO_DOCUMENTO[d.tipo]}
                        {d.dataDoDocumento !== null && ` · ${emBr(d.dataDoDocumento)}`}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {dados.parcelasPagamento.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold text-conteudo-primario">
                Pagamentos ({dados.parcelasPagamento.length})
              </h3>
              <Tabela
                legenda="Parcelas da negociação"
                densidade="compacta"
                colunas={colunas}
                dados={[...dados.parcelasPagamento]}
                chaveDaLinha={(p) => p.id}
                chaveDeCabecalhoDaLinha="natureza"
              />
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
